import React, { useEffect, useState } from "react"
import { Layout } from "src/components/layout"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router"
import { useAuth } from "src/providers/LoginProvider"
import { useRecoilState } from "recoil"
import { Tag, TagService, User, UserService } from "src/openapi"
import { ShowToast } from "../../states/showToast"
import { ShowProgress } from "../../states/showProgress"
import BreadCrumb from "src/components/atom/BreadCrumb"
import MyPageDetailItem from "./MyPageDetailItem"
import { EMPLOYMENT_TYPE, Genders, SIDE_MENU, SNACKBAR_TYPES } from "src/modules/const"
import { Top } from "../top"
import GenerateLineLinkCodeModal from "./GenerateLineLinkCodeModal"
import { CircleAlertIcon } from "lucide-react"
import { Button } from "src/components/ui/button"
import { Card, CardContent, CardHeader } from "src/components/ui/card"

export const MyPageDetail = () => {
  const { t } = useTranslation("translation", { keyPrefix: "employee" })

  const [userInfo, setUserInfo] = useState<User>()

  const [, setShowProgress] = useRecoilState(ShowProgress)
  const [, setShowToast] = useRecoilState(ShowToast)

  const navigate = useNavigate()

  const { user, lineIdToken } = useAuth()
  const [tags, setTags] = useState<Tag[]>([])

  useEffect(() => {
    if (!user) {
      return
    }
    TagService.listTags(user.token, Number(user.companyID))
      .then((res) => {
        setTags(res)
      })
      .catch(() => {
        setShowProgress(false)
      })
  }, [user])

  const idToTag = new Map(tags.map((tag) => [tag.id, tag.tag]))

  const calcAge = (date: string) => {
    let age = new Date().getFullYear() - new Date(date as string).getFullYear()
    return age.toString()
  }

  const getType = (type: string) => {
    let v = ""
    EMPLOYMENT_TYPE.forEach((t) => {
      if (type == t.value) {
        v = t.name
      }
    })
    return v
  }

  useEffect(() => {
    if (!user) {
      return
    }
    setShowProgress(true)
    UserService.getMyProfile(user.token)
      .then((res) => {
        setShowProgress(false)
        setUserInfo(res)

        if (!res.firstLoginStatus) {
          UserService.updateUser(
            user.companyID,
            res.id,
            {
              lastName: res.lastName,
              firstName: res.firstName,
              lastNameKana: res.lastNameKana,
              firstNameKana: res.firstNameKana,
              gender: res.gender,
              genderComment: res.genderComment,
              birthday: res.birthday,
              postCode: res.postCode,
              province: res.province,
              city: res.city,
              address: res.address,
              buildingNo: res.buildingNo,
              phoneNumber: res.phoneNumber,
              joiningDate: res.joiningDate,
              resignDate: res.resignDate,
              status: res.status,
              employmentType: res.employmentType,
              tagIds: res.tagIds,
              firstLoginStatus: true,
              userType: res.userType,
            },
            user.token
          )
            .then(() => {
              let tempUser = userInfo
              tempUser!.firstLoginStatus = true
              setUserInfo(tempUser)
            })
            .catch(() => {})
        }
      })
      .catch(() => {
        setShowProgress(false)
        setShowToast({ show: true, text: t("failed_detail"), type: SNACKBAR_TYPES.ERROR })
      })
  }, [user])

  // 現状LINE認証には対応していないので一律固定文言を返す
  if (lineIdToken) {
    return (
      <div>
        <div className="w-full h-full flex flex-col gap-4">
          <div className="w-full h-[50px] flex justify-center items-center sticky top-0 bg-white p-4">
            <img src="/triple_logo.png" alt="logo" className="h-4" />
          </div>
          <div className="w-full flex flex-col justify-center items-center gap-4 p-4">
            <CircleAlertIcon className="w-16 h-16 text-manual-red bg-white rounded-full" />
            <p className="text-md">
              このままアプリを閉じて管理者から共有されたリンクから再度アクセスしてください。
            </p>
          </div>
        </div>
      </div>
    )
  }

  if (!user) {
    return <Top />
  }

  return (
    <Layout menu={SIDE_MENU.MY_PAGE}>
      <BreadCrumb
        paths={[
          {
            name: t("my_page"),
            link: "",
          },
        ]}
      />
      <Card className="m-4 lg:m-6">
        <CardHeader className="pb-4">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="w-1 h-8 bg-[#00c298] rounded-full" />
              <h2 className="text-lg font-medium">
                {userInfo?.lastName == undefined || userInfo?.firstName == undefined
                  ? ""
                  : `${userInfo?.lastName} ${userInfo?.firstName} （${userInfo?.lastNameKana} ${userInfo?.firstNameKana}）`}
              </h2>
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full lg:w-auto">
              <GenerateLineLinkCodeModal>
                <Button
                  variant="outline"
                  className="w-full sm:w-auto border-[#00c298] text-[#00c298] hover:bg-[#00c298]/10"
                >
                  LINEアカウント紐付け
                </Button>
              </GenerateLineLinkCodeModal>
              <Button
                className="bg-[#00c298] hover:bg-[#00a080] text-white w-full sm:w-auto"
                onClick={() => {
                  navigate(`/mypage/edit`)
                }}
              >
                {t("edit")}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y divide-gray-100">
            <MyPageDetailItem label={t("ID")} value={userInfo?.employeeId!.toString()} />
            <MyPageDetailItem
              label={t("gender")}
              value={
                userInfo
                  ? userInfo?.gender == Genders.OTHER
                    ? userInfo?.genderComment
                    : userInfo?.gender == Genders.MALE
                      ? t("male")
                      : t("female")
                  : ""
              }
            />
            <MyPageDetailItem label={t("birthday")} value={userInfo?.birthday} />
            <MyPageDetailItem
              label={t("age")}
              value={userInfo?.birthday ? calcAge(userInfo?.birthday!) : ""}
            />
            <MyPageDetailItem label={t("email_address")} value={userInfo?.email} />
            <MyPageDetailItem label={t("post_code")} value={userInfo?.postCode} />
            <MyPageDetailItem label={t("province")} value={userInfo?.province} />
            <MyPageDetailItem label={t("city")} value={userInfo?.city} />
            <MyPageDetailItem label={t("street")} value={userInfo?.address} />
            <MyPageDetailItem label={t("building")} value={userInfo?.buildingNo} />
            <MyPageDetailItem label={t("phone_number")} value={userInfo?.phoneNumber} />
            <MyPageDetailItem label={t("join_company_date")} value={userInfo?.joiningDate} />
            <MyPageDetailItem label={t("leave_company_date")} value={userInfo?.resignDate} />
            <MyPageDetailItem
              label={t("employment_type")}
              value={getType(userInfo?.employmentType!)}
            />
            <MyPageDetailItem
              label={t("tag")}
              tags={userInfo?.tagIds ? userInfo?.tagIds.map((id) => idToTag.get(id)) : undefined}
            />
          </div>
        </CardContent>
      </Card>
    </Layout>
  )
}
